// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Alert, Button, Stack, Text } from '@mantine/core';
import type { Patient } from '@medplum/fhirtypes';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useState } from 'react';
import { getIntakeMedplumClient } from '../../lib/bot-client';
import { botIdentifier } from '../../lib/bot-identifier';
import { SettingsSection } from './SettingsSection';

function getPatientEmail(patient: Patient): string {
  return patient.telecom?.find((t) => t.system === 'email')?.value?.trim() ?? '';
}

interface PasswordSectionProps {
  patient: Patient;
}

export function PasswordSection({ patient }: PasswordSectionProps): JSX.Element {
  const email = getPatientEmail(patient);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleRequestReset(): Promise<void> {
    if (!email) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const client = await getIntakeMedplumClient();
      await client.executeBot(botIdentifier('request-patient-password-reset'), { email }, 'application/json');
      setSubmitted(true);
    } catch (err) {
      Sentry.captureException(err);
      setError('We could not send a reset link right now. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <SettingsSection
      title="Password"
      editing={false}
      onEdit={() => undefined}
      onSave={() => undefined}
      onCancel={() => undefined}
      saving={loading}
      actions={
        <Button size="xs" onClick={handleRequestReset} loading={loading} disabled={!email}>
          {submitted ? 'Send again' : 'Email reset link'}
        </Button>
      }
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          We&apos;ll email a secure link to reset the password for your portal account.
        </Text>
        {!email && (
          <Alert color="yellow" title="No email on file">
            Add an email address to your profile before requesting a password reset.
          </Alert>
        )}
        {submitted && email && (
          <Alert color="blue" title="Check your email">
            We&apos;ve sent a password reset link to the email address on file.
          </Alert>
        )}
        {error && (
          <Alert color="red" title="Unable to send reset link">
            {error}
          </Alert>
        )}
      </Stack>
    </SettingsSection>
  );
}
